/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// In-chat meeting card — one per MEETING_CREATE row (types-and-schemas.md
// §2.13). The timeline row owns the card; all live state (title, time, RSVPs,
// canceled) resolves from the host's `MeetingState` projection, so an update or
// cancel re-renders the card without touching the persisted row. Matches the
// iOS `MeetingCardView` + the web Figma: calendar glyph + "MEETING" label +
// title, time / location / reminder rows, a "Starts in …" relative line,
// description, Open/Copy link + creator edit/cancel, and an RSVP row
// (Yes / Maybe / No) with live counts.
//
// className-first throughout (packages/ui styling rule); the only inline styles
// are the thread-panel `maxWidth` (computed) and lucide glyph `color` props
// (lucide takes a `color` prop, not a className), resolved from the class-aware
// `useColorScheme` — NEVER react-native's OS-based one.

import type React from 'react';
import { useCallback } from 'react';
import { Pressable, Text, View } from 'react-native';
import {
  Bell,
  Calendar,
  Check,
  CheckCheck,
  Clock,
  Copy,
  Link2,
  MapPin,
  Pencil,
  Phone,
  Trash2,
  User,
  Video,
  X,
} from 'lucide-react-native';

import { useColorScheme } from '../foundations/colorScheme';
import { useTileMaxWidth } from '../chat/tileConstraint';
import type { MeetingRsvpResponse } from '../appEvent/types';
import type { MeetingStatus, RsvpCounts } from './meetingState';

/** The display content the card needs — the host resolves this from its live
 *  `MeetingState` projection (or the raw create body for the display-only
 *  fallback). Mirrors `PollViewContent`. */
export interface MeetingViewContent {
  readonly meetingId: string;
  readonly title: string;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly location?: string | undefined;
  readonly description?: string | undefined;
  readonly meetingLink?: string | undefined;
  /** Device-local reminder (minutes before). Not on the wire. */
  readonly reminderMinutes?: number | undefined;
  /** Present for a `call`-type meeting — enables the "Join call" action. */
  readonly autoCall?: { readonly media: 'audio' | 'video' } | undefined;
  readonly status: MeetingStatus;
  /** Whether the local user created this meeting (enables edit / cancel). */
  readonly isCreator: boolean;
}

export interface MeetingViewProps {
  readonly content: MeetingViewContent;
  readonly counts: RsvpCounts;
  readonly myRsvp?: MeetingRsvpResponse | undefined;
  readonly onRsvp?: ((response: MeetingRsvpResponse) => void) | undefined;
  readonly onOpenLink?: ((url: string) => void) | undefined;
  readonly onCopyLink?: ((url: string) => void) | undefined;
  readonly onEdit?: (() => void) | undefined;
  readonly onCancelMeeting?: (() => void) | undefined;
  /** Open the responses list (who RSVP'd yes/maybe/no). */
  readonly onShowResponses?: (() => void) | undefined;
  /** Join / start the Ping call attached to a `call`-type meeting. */
  readonly onJoinCall?: ((media: 'audio' | 'video') => void) | undefined;
  readonly isSelf?: boolean | undefined;
  readonly delivered?: boolean | undefined;
  /** Clock label ("9:46 PM") for the delivery footer. */
  readonly timeLabel?: string | undefined;
}

const RSVP_ORDER: ReadonlyArray<MeetingRsvpResponse> = ['yes', 'maybe', 'no'];

export const MeetingView: React.FC<MeetingViewProps> = ({
  content,
  counts,
  myRsvp,
  onRsvp,
  onOpenLink,
  onCopyLink,
  onEdit,
  onCancelMeeting,
  onShowResponses,
  onJoinCall,
  isSelf = false,
  delivered = false,
  timeLabel,
}) => {
  const scheme = useColorScheme();
  const tileMax = useTileMaxWidth();
  const canceled = content.status === 'canceled';
  const past = content.status === 'past';
  const inProgress = content.status === 'inProgress';
  const autoCall = content.autoCall;

  const v = isSelf
    ? {
        card: 'bg-[#537EFF]',
        label: 'text-white/70',
        title: 'text-white',
        detail: 'text-white/80',
        accent: 'text-white',
        divider: 'bg-white/20',
        chip: 'bg-white/15',
        chipActive: 'bg-white',
        chipText: 'text-white',
        chipActiveText: 'text-[#537EFF]',
        linkPrimary: 'bg-white',
        linkPrimaryText: 'text-[#537EFF]',
        linkSecondary: 'bg-white/15',
      }
    : {
        card: 'bg-white dark:bg-[#15171C]',
        label: 'text-light-text-secondary dark:text-white/60',
        title: 'text-light-text-primary dark:text-[#F5F6F8]',
        detail: 'text-light-text-secondary dark:text-white/70',
        accent: 'text-[#537EFF] dark:text-[#7C9BFF]',
        divider: 'bg-light-surface-tertiary dark:bg-[#222429]',
        chip: 'bg-light-surface-tertiary dark:bg-[#222429]',
        chipActive: 'bg-[#537EFF]',
        chipText: 'text-light-text-primary dark:text-[#F5F6F8]',
        chipActiveText: 'text-white',
        linkPrimary: 'bg-[#537EFF]',
        linkPrimaryText: 'text-white',
        linkSecondary: 'bg-light-surface-tertiary dark:bg-[#222429]',
      };

  const glyph = isSelf
    ? 'rgba(255,255,255,0.85)'
    : scheme === 'dark'
      ? '#8C929E'
      : '#6B7280';
  const accentGlyph = isSelf ? '#FFFFFF' : scheme === 'dark' ? '#7C9BFF' : '#537EFF';

  const link = content.meetingLink;
  const handleOpen = useCallback(() => {
    if (link !== undefined && onOpenLink !== undefined) onOpenLink(link);
  }, [link, onOpenLink]);
  const handleCopy = useCallback(() => {
    if (link !== undefined && onCopyLink !== undefined) onCopyLink(link);
  }, [link, onCopyLink]);

  return (
    <View
      // `mt-xs` (not `my-xs`): the bubble tail is an `::after` on the inline-
      // block wrapper, so a BOTTOM margin here pushes the tail ~4px below the
      // card. Top-only margin keeps row spacing while the tail attaches flush.
      className={`w-[300px] max-w-full mt-xs p-3 rounded-2xl ${v.card} ${
        canceled || past ? 'opacity-75' : ''
      }`}
      style={tileMax !== null ? { maxWidth: tileMax } : undefined}
      accessibilityLabel={`Meeting: ${content.title}`}
    >
      {/* Header */}
      <View className="flex-row items-start gap-2">
        <View className="mt-0.5">
          <Calendar size={18} strokeWidth={2} color={accentGlyph} />
        </View>
        <View className="flex-1">
          <Text className={`text-[10px] font-semibold tracking-wide ${v.label}`}>
            {canceled ? 'MEETING · CANCELED' : 'MEETING'}
          </Text>
          <Text
            className={`text-base font-bold ${v.title} ${canceled ? 'line-through' : ''}`}
            numberOfLines={2}
          >
            {content.title}
          </Text>
        </View>
        {content.isCreator && !canceled ? (
          <View className="flex-row items-center gap-1">
            {onEdit !== undefined ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Edit meeting"
                onPress={onEdit}
                className="h-7 w-7 items-center justify-center rounded-lg active:opacity-70"
              >
                <Pencil size={15} strokeWidth={2} color={glyph} />
              </Pressable>
            ) : null}
            {onCancelMeeting !== undefined ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Cancel meeting"
                onPress={onCancelMeeting}
                className="h-7 w-7 items-center justify-center rounded-lg active:opacity-70"
              >
                <Trash2 size={15} strokeWidth={2} color={glyph} />
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </View>

      {/* Detail rows */}
      <View className="mt-2 gap-1.5">
        <DetailRow icon={<Clock size={13} strokeWidth={2} color={glyph} />} textClass={v.detail}>
          {formatTimeRange(content.startsAt, content.endsAt)}
        </DetailRow>
        {content.location !== undefined && content.location.length > 0 ? (
          <DetailRow icon={<MapPin size={13} strokeWidth={2} color={glyph} />} textClass={v.detail}>
            {content.location}
          </DetailRow>
        ) : null}
        {content.reminderMinutes !== undefined && !canceled && !past ? (
          <DetailRow icon={<Bell size={13} strokeWidth={2} color={glyph} />} textClass={v.detail}>
            {reminderLabel(content.reminderMinutes)}
          </DetailRow>
        ) : null}
      </View>

      {/* Relative "Starts in …" / status line */}
      {!canceled ? (
        <Text className={`mt-2 text-xs font-semibold ${v.accent}`}>
          {inProgress
            ? 'In progress'
            : past
              ? 'Ended'
              : relativeStart(content.startsAt)}
        </Text>
      ) : null}

      {/* Description */}
      {content.description !== undefined && content.description.length > 0 ? (
        <Text className={`mt-2 text-xs leading-[17px] ${v.detail}`} numberOfLines={4}>
          {content.description}
        </Text>
      ) : null}

      {/* Link actions */}
      {link !== undefined && link.length > 0 && !canceled ? (
        <View className="mt-3 flex-row items-center gap-2">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Open meeting link"
            onPress={handleOpen}
            className={`h-9 flex-1 flex-row items-center justify-center gap-1.5 rounded-full ${v.linkPrimary} active:opacity-80`}
          >
            <Link2 size={15} strokeWidth={2} color={isSelf ? '#537EFF' : '#FFFFFF'} />
            <Text className={`text-[13px] font-semibold ${v.linkPrimaryText}`}>Open link</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Copy meeting link"
            onPress={handleCopy}
            className={`h-9 flex-1 flex-row items-center justify-center gap-1.5 rounded-full ${v.linkSecondary} active:opacity-80`}
          >
            <Copy size={15} strokeWidth={2} color={glyph} />
            <Text className={`text-[13px] font-semibold ${v.chipText}`}>Copy link</Text>
          </Pressable>
        </View>
      ) : null}

      {/* Join call — only for a `call`-type meeting that hasn't ended. */}
      {autoCall !== undefined && onJoinCall !== undefined && !canceled && !past ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={inProgress ? 'Join call' : 'Start call'}
          onPress={() => onJoinCall(autoCall.media)}
          className={`mt-3 h-9 flex-row items-center justify-center gap-1.5 rounded-full ${v.linkPrimary} active:opacity-80`}
        >
          {autoCall.media === 'video' ? (
            <Video size={15} strokeWidth={2} color={isSelf ? '#537EFF' : '#FFFFFF'} />
          ) : (
            <Phone size={15} strokeWidth={2} color={isSelf ? '#537EFF' : '#FFFFFF'} />
          )}
          <Text className={`text-[13px] font-semibold ${v.linkPrimaryText}`}>
            {inProgress ? 'Join call' : autoCall.media === 'video' ? 'Start video call' : 'Start call'}
          </Text>
        </Pressable>
      ) : null}

      {/* RSVP */}
      {!canceled ? (
        <>
          <View className={`mt-3 h-px ${v.divider}`} />
          <Text className={`mt-3 text-[10px] font-semibold tracking-wide ${v.label}`}>
            ARE YOU GOING?
          </Text>
          <View className="mt-2 flex-row items-center gap-2">
            {RSVP_ORDER.map((response) => {
              const mine = myRsvp === response;
              return (
                <Pressable
                  key={response}
                  accessibilityRole="button"
                  accessibilityLabel={`RSVP ${rsvpLabel(response)}`}
                  disabled={past || onRsvp === undefined}
                  onPress={() => onRsvp?.(response)}
                  className={`h-9 flex-1 flex-row items-center justify-center gap-1.5 rounded-full ${
                    mine ? v.chipActive : v.chip
                  } ${past || onRsvp === undefined ? 'opacity-50' : 'active:opacity-80'}`}
                >
                  {rsvpIcon(response, mine ? (isSelf ? '#537EFF' : '#FFFFFF') : glyph)}
                  <Text
                    className={`text-[13px] font-semibold ${mine ? v.chipActiveText : v.chipText}`}
                  >
                    {rsvpLabel(response)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
          <Pressable
            onPress={onShowResponses}
            disabled={onShowResponses === undefined}
            accessibilityRole="button"
            accessibilityLabel="View responses"
            className={onShowResponses !== undefined ? 'active:opacity-60' : ''}
          >
            <Text className={`mt-2 text-[11px] ${v.label} ${onShowResponses !== undefined ? 'underline' : ''}`}>
              {`${counts.yes} going · ${counts.maybe} maybe · ${counts.no} no`}
            </Text>
          </Pressable>
        </>
      ) : null}

      {/* Delivery footer — clock + read-tick (self only), mirroring PollView so
          a meeting card shows the same sent/delivered affordance as every other
          self bubble. */}
      {timeLabel !== undefined && timeLabel.length > 0 ? (
        <View className="mt-2 flex-row items-center justify-end gap-0.5">
          <Text className={`text-[10px] leading-[14px] ${v.label}`}>{timeLabel}</Text>
          {isSelf && delivered ? (
            <CheckCheck size={12} strokeWidth={2} color="rgba(255,255,255,0.85)" />
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const DetailRow: React.FC<{
  readonly icon: React.ReactNode;
  readonly textClass: string;
  readonly children: React.ReactNode;
}> = ({ icon, textClass, children }) => (
  <View className="flex-row items-center gap-2">
    <View className="w-3.5 items-center">{icon}</View>
    <Text className={`flex-1 text-xs ${textClass}`} numberOfLines={2}>
      {children}
    </Text>
  </View>
);

function rsvpLabel(r: MeetingRsvpResponse): string {
  return r === 'yes' ? 'Yes' : r === 'maybe' ? 'Maybe' : 'No';
}

function rsvpIcon(r: MeetingRsvpResponse, color: string): React.ReactNode {
  if (r === 'yes') return <Check size={15} strokeWidth={2.5} color={color} />;
  if (r === 'maybe') return <User size={15} strokeWidth={2} color={color} />;
  return <X size={15} strokeWidth={2.5} color={color} />;
}

function reminderLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes before`;
  if (minutes < 1440) {
    const h = Math.round(minutes / 60);
    return `${h} hour${h === 1 ? '' : 's'} before`;
  }
  const d = Math.round(minutes / 1440);
  return `${d} day${d === 1 ? '' : 's'} before`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatClock(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
}

/** "Wednesday, July 29, 2026 at 6:00 PM – 5:22 PM" (same day) or a full range. */
export function formatTimeRange(startsAt: number, endsAt: number): string {
  const s = new Date(startsAt);
  const e = new Date(endsAt);
  const startStr = `${WEEKDAYS[s.getDay()]}, ${MONTHS[s.getMonth()]} ${s.getDate()}, ${s.getFullYear()} at ${formatClock(s)}`;
  const sameDay =
    s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth() && s.getDate() === e.getDate();
  const endStr = sameDay
    ? formatClock(e)
    : `${MONTHS[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()} at ${formatClock(e)}`;
  return `${startStr} – ${endStr}`;
}

/** "Starts in 1 day" / "Starts in 3 hours" / "Starts in 15 minutes". */
export function relativeStart(startsAt: number, now: number = Date.now()): string {
  const deltaMin = Math.round((startsAt - now) / 60000);
  if (deltaMin <= 0) return 'Starting now';
  if (deltaMin < 60) return `Starts in ${deltaMin} minute${deltaMin === 1 ? '' : 's'}`;
  const hours = Math.round(deltaMin / 60);
  if (hours < 24) return `Starts in ${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `Starts in ${days} day${days === 1 ? '' : 's'}`;
}
